import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material'
import Stack from '@mui/material/Stack'

// components
import StyledTable from '../../components/ReactTable/StyledTable'
import StyledTableContainer from '../../components/StyledTableContainer/StyledTableContainer'
import StyledEmptyHeader from '../../components/StyledEmptyHeader/StyledEmptyHeader'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'
import { useDateSelectorContext } from '../../context/DateSelectorContext'

// services
import liveDataService from '../../service/liveData.service'

const BoxContainer = styled('div')(() => ({
  width: '100%',
  height: '70vh',
  overflow: 'auto',
  border: '1px solid #ccc',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}))

const EnergyRecord = () => {
  const [energyRecordRows, setEnergyRecordRows] = useState([])
  const [selectedMachine, setSelectedMachine] = useState({
    label: '',
    value: ''
  })
  const { selectedDate } = useDateSelectorContext()
  const [availableShifts, setAvailableShifts] = useState([])
  const [availableMachines, setAvailableMachines] = useState([])
  const [selectedShift, setSelectedShift] = useState({
    label: 'All shifts',
    value: 0
  })
  const [isLoading, setIsLoading] = useState(false)

  const fetchEnergyRecords = async () => {
    const params = {}
    if (selectedDate) params.date = selectedDate

    try {
      setIsLoading(true)
      const energyRecordResponse =
        await liveDataService.getEnergyRecords(params)

      const data = energyRecordResponse.data.data || []

      const availableShifts = []
      const availableMachinesList = []

      const partsData = data.map((item, i) => {
        const shiftValue = `S${item.shift}`
        if (!availableShifts.includes(shiftValue)) {
          availableShifts.push(shiftValue)
        }

        if (!availableMachinesList.includes(item.machine)) {
          availableMachinesList.push(item.machine)
        }

        return {
          index: i + 1,
          machine: item.machine,
          time: item.time,
          shift: shiftValue,
          tag1: item.tag1,
          tag2: item.tag2,
          tag3: item.tag3,
          tag4: item.tag4,
          tag5: item.tag5,
          tag6: item.tag6
        }
      })

      const machineOptions = availableMachinesList.map((machine) => ({
        label: machine,
        value: machine
      }))

      if (machineOptions.length > 0) {
        setSelectedMachine((prev) => {
          const exists = machineOptions.find((m) => m.value === prev.value)
          return exists || machineOptions[0]
        })
      }

      setAvailableMachines(machineOptions)
      setAvailableShifts([
        { label: 'All shifts', value: 0 },
        ...availableShifts.map((shift) => ({
          label: shift,
          value: shift
        }))
      ])
      setEnergyRecordRows(partsData)
    } catch (err) {
      console.log(err)
      setEnergyRecordRows([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchEnergyRecords()
  }, [selectedDate])

  const searchObj = {
    ...(selectedMachine &&
      selectedMachine.value && {
        machine: selectedMachine.value
      }),
    ...(selectedShift &&
      selectedShift.value &&
      selectedShift.value !== 0 && {
        shift: selectedShift.value
      })
  }

  //2. Extract the keys into an array
  const keysToChecked = Object.keys(searchObj)
  console.log('keysToChecked', keysToChecked, searchObj)

  //3. filter
  let filteredRows = energyRecordRows
  if (keysToChecked.length > 0) {
    filteredRows = energyRecordRows.filter((row) =>
      keysToChecked.every((key) => searchObj[key] === row[key])
    )
  }

  const formatToTwoDecimalPlaces = (value) =>
    typeof value === 'number' ? value.toFixed(2) : value

  const energyRecordColumns = [
    {
      Header: 'S.No',
      accessor: 'index'
    },
    {
      Header: 'Machine',
      accessor: 'machine'
    },
    {
      Header: 'Time',
      accessor: 'time'
    },
    {
      Header: 'Shift',
      accessor: 'shift'
    },
    {
      Header: 'Voltge [V]',
      accessor: 'tag1',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Current [A]',
      accessor: 'tag2',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Power [Kw]',
      accessor: 'tag3',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },

    {
      Header: 'Frequency [Hz]',
      accessor: 'tag4',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Energy [KWh]',
      accessor: 'tag5',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Power Factor',
      accessor: 'tag6',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    }
  ]

  const TotalEnergyDisplay = ({ filteredRows }) => {
    // Get the first and last energy values from filtered rows
    const firstEnergy =
      filteredRows.length > 0
        ? parseFloat(filteredRows[filteredRows.length - 1].tag5)
        : 0
    const lastEnergy =
      filteredRows.length > 0 ? parseFloat(filteredRows[0].tag5) : 0

    // Calculate the difference
    const energyDifference = (lastEnergy - firstEnergy).toFixed(3)

    return (
      <div style={{ marginTop: '2px' }}>
        <strong>Energy Difference:</strong> {energyDifference} KWh
      </div>
    )
  }

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          padding: '16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #eeeeee'
        }}
      >
        <StyledSelectField
          label=""
          placeHolderText="Select shift"
          selectedValue={selectedShift}
          selectOptions={availableShifts}
          onChangeHandler={(value) => setSelectedShift(value)}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No shift found"
        />
        <StyledSelectField
          label=""
          placeHolderText=""
          selectedValue={selectedMachine}
          selectOptions={availableMachines}
          onChangeHandler={(value) => setSelectedMachine(value)}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No machine found"
        />
      </Stack>

      <Stack sx={{ gap: '16px', padding: '16px' }}>
        <StyledTableContainer>
          {isLoading && <StyledEmptyHeader>Loading...</StyledEmptyHeader>}
          {!isLoading && filteredRows.length === 0 && (
            <StyledEmptyHeader>There are no energy record</StyledEmptyHeader>
          )}
          <TotalEnergyDisplay filteredRows={filteredRows} />
          {!isLoading && filteredRows.length > 0 && (
            <BoxContainer>
              <StyledTable
                pagination={false}
                columns={energyRecordColumns}
                data={filteredRows}
                hiddenColumns={['machine', 'index']}
              />
            </BoxContainer>
          )}
        </StyledTableContainer>
      </Stack>
    </>
  )
}

export default EnergyRecord
